import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ApiError } from '../api/client'
import { useEvent } from '../features/events/api'
import { useCreatePlan } from '../features/plans/api'
import { useChecklistTemplates } from '../features/templates/checklistApi'
import { selectable } from '../features/templates/selectable'
import { useTimelineTemplates } from '../features/templates/timelineApi'

/**
 * Composes a plan for an event.
 *
 * Only published, non-retired templates are offered: a plan records exactly
 * what was published, and a draft could still change underneath it.
 */
export function PlanCreatePage() {
  const { eventId = '' } = useParams()
  const navigate = useNavigate()
  const { data: event } = useEvent(eventId)
  const { data: timelines, isPending: timelinesPending } = useTimelineTemplates()
  const { data: checklists, isPending: checklistsPending } = useChecklistTemplates()
  const createPlan = useCreatePlan(eventId)

  const [timelineId, setTimelineId] = useState('')
  const [checklistIds, setChecklistIds] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)

  const availableTimelines = selectable(timelines)
  const availableChecklists = selectable(checklists)

  const toggleChecklist = (id: string) =>
    setChecklistIds((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    )

  const submit = async (submitEvent: React.FormEvent) => {
    submitEvent.preventDefault()
    setError(null)

    try {
      await createPlan.mutateAsync({
        timelineTemplateId: timelineId,
        checklistTemplateIds: checklistIds,
      })
      navigate(`/events/${eventId}`)
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'Could not create the plan.')
    }
  }

  if (timelinesPending || checklistsPending) {
    return <p>Loading templates…</p>
  }

  return (
    <section>
      <h1>Compose a plan{event ? ` for ${event.name}` : ''}</h1>
      <p className="muted">
        The plan copies these templates as they are today. Publishing a new version of either later
        will not change this plan.
      </p>

      {error ? (
        <p role="alert" className="error">
          {error}
        </p>
      ) : null}

      <form className="form" onSubmit={submit}>
        <fieldset className="fieldset">
          <legend>Timeline template</legend>
          {availableTimelines.length === 0 ? (
            <p className="empty">
              No published timeline templates. Publish one before composing a plan.
            </p>
          ) : (
            availableTimelines.map((template) => (
              <label key={template.id} className="checkbox">
                <input
                  type="radio"
                  name="timeline"
                  value={template.id}
                  checked={timelineId === template.id}
                  onChange={() => setTimelineId(template.id)}
                />
                {template.name} (v{template.version}, {template.milestoneCount} milestones)
              </label>
            ))
          )}
        </fieldset>

        <fieldset className="fieldset">
          <legend>Checklist templates</legend>
          {availableChecklists.length === 0 ? (
            <p className="empty">No published checklist templates.</p>
          ) : (
            availableChecklists.map((template) => (
              <label key={template.id} className="checkbox">
                <input
                  type="checkbox"
                  checked={checklistIds.includes(template.id)}
                  onChange={() => toggleChecklist(template.id)}
                />
                {template.name} (v{template.version}, {template.itemCount} items,{' '}
                {template.defaultPhaseDisplayName})
              </label>
            ))
          )}
        </fieldset>

        <div className="form-actions">
          <button
            type="submit"
            className="primary"
            disabled={!timelineId || createPlan.isPending}
          >
            {createPlan.isPending ? 'Creating…' : 'Create plan'}
          </button>
          <button type="button" onClick={() => navigate(`/events/${eventId}`)}>
            Cancel
          </button>
        </div>
      </form>
    </section>
  )
}
